import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { KeyRound, Loader2, Plus, Pencil, Trash2, Eye, EyeOff, Copy, ExternalLink, Folder, FolderPlus, Users } from "lucide-react";

const EMPTY_FORM = { channel_name: "", url: "", username: "", password: "", remark: "", property_id: "none", folder_id: "none" };

const chipCls = (active) =>
  `inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
    active ? "border-amber-500/50 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-900 text-slate-400 hover:text-slate-200"
  }`;

function FolderDialog({ folder, onSaved }) {
  const isEdit = !!folder;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(folder?.name || ""); setShared(folder?.shared || false); }
  }, [open, folder]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await api.put(`/channels/folders/${folder.id}`, { name, shared });
      else await api.post("/channels/folders", { name, shared });
      toast.success(isEdit ? "Folder diperbarui" : "Folder dibuat");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <button className="text-slate-500 hover:text-amber-400" data-testid={`edit-folder-${folder.id}`}><Pencil className="h-3 w-3" /></button>
        ) : (
          <Button size="sm" variant="outline" className="border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700" data-testid="add-folder-btn"><FolderPlus className="h-3.5 w-3.5" /> Folder</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit folder" : "Folder baru"}</DialogTitle>
          <DialogDescription>Kelompokkan channel ke folder pribadi Anda atau folder bersama tim.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nama folder</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Misal: Villa Seminyak, Musiman…" data-testid="folder-name-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="folder-shared-check">
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} className="accent-amber-500" />
            Folder bersama — semua tim bisa melihat
          </label>
          <Button type="submit" className="w-full" disabled={saving} data-testid="folder-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Simpan" : "Buat folder"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChannelDialog({ channel, properties, folders = [], onSaved, triggerTestId }) {
  const isEdit = !!channel;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(isEdit
        ? { channel_name: channel.channel_name, url: channel.url || "", username: channel.username || "", password: channel.password || "", remark: channel.remark || "", property_id: channel.property_id || "none", folder_id: channel.folder_id || "none" }
        : EMPTY_FORM);
    }
  }, [open, channel, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        property_id: form.property_id === "none" ? null : form.property_id,
        folder_id: form.folder_id === "none" ? null : form.folder_id,
      };
      if (isEdit) await api.put(`/channels/${channel.id}`, payload);
      else await api.post("/channels", payload);
      toast.success(isEdit ? "Channel updated" : "Channel added");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <button className="text-slate-500 hover:text-indigo-400" data-testid={triggerTestId}><Pencil className="h-3.5 w-3.5" /></button>
        ) : (
          <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-channel-btn"><Plus className="h-4 w-4" /> Add channel</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit channel" : "Add OTA / channel"}</DialogTitle>
          <DialogDescription>Store the login credentials for this channel.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Channel name</Label>
            <Input value={form.channel_name} onChange={(e) => setForm({ ...form, channel_name: e.target.value })} placeholder="Booking.com, Agoda, Traveloka…" required data-testid="channel-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>URL</Label>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://admin.booking.com" data-testid="channel-url-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="channel-username-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="channel-password-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Property (optional)</Label>
            <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
              <SelectTrigger data-testid="channel-property-select"><SelectValue placeholder="All properties (shared)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">All properties (shared)</SelectItem>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Folder (optional)</Label>
            <Select value={form.folder_id} onValueChange={(v) => setForm({ ...form, folder_id: v })}>
              <SelectTrigger data-testid="channel-folder-select"><SelectValue placeholder="Tanpa folder" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Tanpa folder</SelectItem>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}{f.shared ? " (bersama)" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Remark</Label>
            <Textarea value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} placeholder="Notes, PIC, expiry…" className="resize-none h-16" data-testid="channel-remark-input" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="channel-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Add channel"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordCell({ value, id }) {
  const [show, setShow] = useState(false);
  if (!value) return <span className="text-xs text-slate-500">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs text-slate-200" data-testid={`channel-password-${id}`}>{show ? value : "••••••••"}</span>
      <button onClick={() => setShow(!show)} className="text-slate-500 hover:text-slate-300" data-testid={`channel-toggle-${id}`}>
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
      <button
        onClick={async () => { try { await navigator.clipboard.writeText(value); toast.success("Password copied"); } catch { toast.error("Copy failed"); } }}
        className="text-slate-500 hover:text-slate-300"
        data-testid={`channel-copy-${id}`}
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function ChannelsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [channels, setChannels] = useState([]);
  const [properties, setProperties] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all");
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(true);

  const load = useCallback(async () => {
    try {
      if (!isAdmin) {
        const f = await api.get("/settings/feature-access");
        if (!f.data.flags.staff_view_channels) { setAllowed(false); return; }
      }
      const [c, p, fo] = await Promise.all([api.get("/channels"), api.get("/properties"), api.get("/channels/folders")]);
      setChannels(c.data.channels);
      setProperties(p.data.properties);
      setFolders(fo.data.folders);
    } catch (e) {
      if (e.response?.status === 403) setAllowed(false);
      else toast.error("Failed to load channels");
    }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const delFolder = async (f) => {
    if (!window.confirm(`Hapus folder "${f.name}"? Channel di dalamnya tidak ikut terhapus.`)) return;
    try {
      await api.delete(`/channels/folders/${f.id}`);
      toast.success("Folder dihapus");
      setActiveFolder("all");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this channel?")) return;
    try {
      await api.delete(`/channels/${id}`);
      toast.success("Channel deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const shown = activeFolder === "all" ? channels : channels.filter((c) => (c.folder_id || "") === activeFolder);

  return (
    <div className="min-h-screen bg-[#1F1B16] dark-scroll text-slate-100">
      <AppNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="channels-page">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 text-white">
              <KeyRound className="h-6 w-6 text-amber-400" /> OTA & Channels
            </h1>
            <p className="text-sm text-slate-400">Central vault for channel credentials — Booking.com, Agoda, Traveloka, and others.</p>
          </div>
          {allowed && <ChannelDialog properties={properties} folders={folders} onSaved={load} />}
        </div>

        {allowed && !loading && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="folder-bar">
            <button onClick={() => setActiveFolder("all")} className={chipCls(activeFolder === "all")} data-testid="folder-chip-all">
              <Folder className="h-3.5 w-3.5" /> Semua ({channels.length})
            </button>
            {folders.map((f) => (
              <span key={f.id} className="inline-flex items-center gap-1">
                <button onClick={() => setActiveFolder(f.id)} className={chipCls(activeFolder === f.id)} data-testid={`folder-chip-${f.id}`}
                  title={f.shared ? "Folder bersama (semua tim)" : `Folder pribadi ${f.owner_name}`}>
                  {f.shared ? <Users className="h-3.5 w-3.5" /> : <Folder className="h-3.5 w-3.5" />} {f.name} ({f.count})
                </button>
                {(isAdmin || f.owner_id === user?.id) && activeFolder === f.id && (
                  <>
                    <FolderDialog folder={f} onSaved={load} />
                    <button onClick={() => delFolder(f)} className="text-slate-500 hover:text-rose-400" data-testid={`delete-folder-${f.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </span>
            ))}
            <FolderDialog onSaved={load} />
          </div>
        )}

        {!allowed && (
          <div className="rounded-xl border border-amber-800/50 bg-amber-900/20 p-6 text-center text-sm text-amber-300" data-testid="channels-disabled-notice">
            Access to OTA &amp; Channels is disabled for staff. Ask your admin to enable it in Settings → Staff feature access.
          </div>
        )}
        {allowed && (

        <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
          ) : shown.length === 0 ? (
            <p className="text-sm text-slate-500 py-16 text-center" data-testid="channels-empty">{activeFolder === "all" ? 'No channels yet — click "Add channel" to store the first one.' : "Folder ini masih kosong — pindahkan channel ke folder ini lewat tombol edit."}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="channels-table">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-slate-400">
                    <th className="p-3 font-medium">Channel Name</th>
                    <th className="p-3 font-medium">URL</th>
                    <th className="p-3 font-medium">Username</th>
                    <th className="p-3 font-medium">Password</th>
                    <th className="p-3 font-medium">Property</th>
                    <th className="p-3 font-medium">Folder</th>
                    <th className="p-3 font-medium">Remark</th>
                    <th className="p-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((c) => (
                    <tr key={c.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`channel-row-${c.id}`}>
                      <td className="p-3 font-medium text-white whitespace-nowrap">{c.channel_name}</td>
                      <td className="p-3">
                        {c.url ? (
                          <a href={c.url.startsWith("http") ? c.url : `https://${c.url}`} target="_blank" rel="noreferrer"
                            className="text-indigo-400 hover:underline inline-flex items-center gap-1 text-xs max-w-[180px] truncate" data-testid={`channel-url-${c.id}`}>
                            <ExternalLink className="h-3 w-3 flex-shrink-0" /> {c.url.replace(/^https?:\/\//, "")}
                          </a>
                        ) : <span className="text-xs text-slate-500">—</span>}
                      </td>
                      <td className="p-3 text-slate-300 font-mono text-xs">{c.username || "—"}</td>
                      <td className="p-3"><PasswordCell value={c.password} id={c.id} /></td>
                      <td className="p-3 text-xs whitespace-nowrap">
                        {c.property_name ? (
                          <span className="text-slate-400">{c.property_name}</span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 font-medium" data-testid={`channel-allprops-${c.id}`}>All properties</span>
                        )}
                      </td>
                      <td className="p-3 text-xs whitespace-nowrap">
                        {c.folder_id ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-medium" data-testid={`channel-folder-badge-${c.id}`}>
                            <Folder className="h-3 w-3" /> {folders.find((f) => f.id === c.folder_id)?.name || "—"}
                          </span>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="p-3 text-xs text-slate-400 max-w-[200px] truncate" title={c.remark}>{c.remark || "—"}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <ChannelDialog channel={c} properties={properties} folders={folders} onSaved={load} triggerTestId={`edit-channel-${c.id}`} />
                        {isAdmin && (
                          <button onClick={() => del(c.id)} className="text-slate-500 hover:text-rose-400 ml-2" data-testid={`delete-channel-${c.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </main>
    </div>
  );
}
